import {zodResolver} from '@hookform/resolvers/zod'
import {Resolver} from 'react-hook-form'
import {z} from 'zod'

import {confirmationPinSchema} from '../fields/confirmationPin/schema'
import {dateTimeSchema} from '../fields/dateTime/schema'
import {validateDateTime} from '../fields/dateTime/validation/validateDateTime'
import {validateLoadSpacing, loadSpacingSchema} from '../fields/loadSpacing/validation'
import {mixFormSchema} from '../fields/mix/schema'
import {validateMaterial} from '../fields/mix/validation'
import {
  mixCharacteristicsSchema,
  validateMixCharacteristics
} from '../fields/mixCharacteristics/validation'
import {orderPlacerSchema} from '../fields/orderPlacer/schema'
import {validateOrderPlacer} from '../fields/orderPlacer/validate'
import {loadBalanceSchema, validateLoadBalance} from '../fields/loadBalance/validation'
import {plusLoadSchema} from '../fields/plusLoad/validation/schema'
import {validatePlusLoad} from '../fields/plusLoad/validation/validatePlusLoad'
import {QuantityForm} from '../fields/quantity/types'
import {quantitySchema} from '../fields/quantity/validation/schema'
import {validateQuantity} from '../fields/quantity/validation/validateQuantity'
import {retarderSchema, validateRetarder} from '../fields/retarder/validation'
import {siteContactSchema} from '../fields/siteContact/schema'
import {validateSiteContact} from '../fields/siteContact/validate'
import {
  additionalNotesSchema,
  poNumberSchema,
  driverInstructionsSchema
} from '../fields/textField/schema'
import {AdditionalNotesForm, DriverInstructionsForm, PoNumberForm} from '../fields/textField/types'
import {
  validateAdditionalNotes,
  validateDriverInstructions,
  validatePoNumber
} from '../fields/textField/validation/validateTextField'
import {FieldsConfiguration, FieldsForm} from '../fields/types'
import {unloadingMethodFormSchema} from '../fields/unloadingMethod/schema'
import {validateUnloadingMethod} from '../fields/unloadingMethod/validation'
import {concreteUsageSchema} from '../fields/usage/schema'
import {validateConcreteUsage} from '../fields/usage/validation'
import {FormValidationInput} from '../types'

// fields/trucks/schema.ts
const trucksSchema = z.object({
  truckType: z.union([z.string(), z.undefined()]),
  isMultipleTruck: z.boolean()
})

// fields/isConfirmedOrder/schema.ts
const isConfirmedOrderSchema = z.object({
  isConfirmedOrder: z.union([z.boolean(), z.undefined()])
})

const orderProcessFormSchema = (input: FormValidationInput<FieldsConfiguration>) => {
  const baseSchema = z.object({
    ...quantitySchema.shape,
    ...plusLoadSchema.shape,
    ...loadBalanceSchema.shape,
    ...loadSpacingSchema.shape,
    ...trucksSchema.shape,
    ...isConfirmedOrderSchema.shape,
    ...confirmationPinSchema.shape,
    ...retarderSchema.shape,
    ...dateTimeSchema.shape,
    ...mixFormSchema.shape,
    ...mixCharacteristicsSchema.shape,
    ...siteContactSchema.shape,
    ...orderPlacerSchema.shape,
    ...concreteUsageSchema.shape,
    ...unloadingMethodFormSchema.shape,
    ...additionalNotesSchema.shape,
    ...poNumberSchema.shape,
    ...driverInstructionsSchema.shape
  })

  const schemaWithRefine = baseSchema.superRefine((data, ctx) => {
    const fieldsToValidate = input.validationContext?.fieldsToValidate || []
    const isFieldSpecific = input.validationContext?.isFieldSpecificValidation || false

    // If this is field-specific validation, only run relevant validators
    if (isFieldSpecific && fieldsToValidate.length > 0) {
      // Check if material fields are being validated
      if (fieldsToValidate.some((field) => field.includes('material'))) {
        validateMaterial(data, ctx)
      }

      // Check if material variations/mix characteristics are being validated
      if (fieldsToValidate.some((field) => field.includes('materialVariations'))) {
        validateMixCharacteristics(data, ctx, input)
      }

      // Check if quantity fields are being validated
      if (fieldsToValidate.some((field) => field.includes('quantity'))) {
        validateQuantity(data as QuantityForm, ctx, input)
      }

      if (fieldsToValidate.some((field) => field.includes('maxLoadSize'))) {
        validateLoadBalance(data as any, ctx, input)
      }

      // Check if concrete usage is being validated
      if (fieldsToValidate.some((field) => field.includes('concreteUsage'))) {
        validateConcreteUsage(data, ctx, input)
      }

      // Check if unloading method is being validated
      if (fieldsToValidate.some((field) => field.includes('unloadingMethod'))) {
        validateUnloadingMethod(data, ctx, input)
      }

      // Check if load spacing is being validated
      if (fieldsToValidate.some((field) => field.includes('loadSpacing'))) {
        validateLoadSpacing(data, ctx, input)
      }

      // Check if text fields are being validated
      if (fieldsToValidate.some((field) => field.includes('additionalNotes'))) {
        validateAdditionalNotes(data as AdditionalNotesForm, ctx, input)
      }

      if (fieldsToValidate.some((field) => field.includes('poNumber'))) {
        validatePoNumber(data as PoNumberForm, ctx, input)
      }

      if (fieldsToValidate.some((field) => field.includes('driverInstructions'))) {
        validateDriverInstructions(data as DriverInstructionsForm, ctx, input)
      }

      // Check if contact fields are being validated
      if (fieldsToValidate.some((field) => field.includes('orderPlacer'))) {
        validateOrderPlacer(data, ctx)
      }

      if (fieldsToValidate.some((field) => field.includes('siteContact'))) {
        validateSiteContact(data, ctx)
      }
    } else {
      // Run all validations for full form validation
      validateRetarder(data, ctx, input)
      validateLoadSpacing(data, ctx, input)
      validateDateTime(data, ctx, input)
      validateSiteContact(data, ctx)
      validateOrderPlacer(data, ctx)
      validateConcreteUsage(data, ctx, input)
      validateUnloadingMethod(data, ctx, input)
      validateMaterial(data, ctx)
      validateMixCharacteristics(data, ctx, input)
      validateQuantity(data as QuantityForm, ctx, input)
      validateLoadBalance(data as any, ctx, input)
      validatePlusLoad(data, ctx, input)
      validateAdditionalNotes(data as AdditionalNotesForm, ctx, input)
      validatePoNumber(data as PoNumberForm, ctx, input)
      validateDriverInstructions(data as DriverInstructionsForm, ctx, input)
    }
  })

  return schemaWithRefine
}

export const buildResolver: (
  input: FormValidationInput<FieldsConfiguration>
) => Resolver<FieldsForm> = (input) => {
  // Wrap the resolver to pass validation context to the schema
  const wrappedResolver: Resolver<FieldsForm> = async (values, context, options) => {
    // Pass the validation context to the schema
    const schemaWithContext = orderProcessFormSchema({
      ...input,
      validationContext: {
        fieldsToValidate: options?.names || [],
        isFieldSpecificValidation: !!(options?.names && options?.names.length > 0)
      }
    })

    const originalResolverWithContext = zodResolver(schemaWithContext)
    return await originalResolverWithContext(values, context, options)
  }

  return wrappedResolver
}
